import React from 'react';
import { motion, type Variants } from 'framer-motion';
import {
    FileCheck,
    ShieldAlert,
    CreditCard,
    UserCircle2,
    Bot,
    Copyright,
    RefreshCw,
    Mail,
    AlertTriangle,
    CheckCircle2,
    ShieldCheck,
    Gavel,
} from 'lucide-react';

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const SECTIONS = [
    {
        id: 'medical-disclaimer',
        title: '1. Strict Medical Disclaimer',
        icon: ShieldAlert,
        items: [
            {
                label: '1.1 Not Medical Advice',
                text: 'LesslyKilo is a health, fitness, and nutritional tracking tool that uses Artificial Intelligence to provide insights. The Service, including the AI Meal Inspector, Weekly Intelligence, and Interactive AI Coach, is provided for informational and educational purposes only and does not constitute medical advice, diagnosis, or treatment.',
            },
            {
                label: '1.2 No Doctor-Patient Relationship',
                text: 'Although LesslyKilo is designed using health-informed logic and clinical principles, use of the Service does not establish a doctor-patient relationship between you and Viomath Tech Proprietary Limited, its founders, or its affiliates.',
            },
            {
                label: '1.3 Consult Your Physician',
                text: 'Always seek the advice of a physician or other qualified healthcare provider regarding medical conditions, diet, exercise, or treatment decisions. Never disregard professional advice or delay seeking care because of content provided through the Service.',
            },
        ],
    },
    {
        id: 'subscriptions-billing',
        title: '2. Subscriptions, Free Trials, and Billing',
        icon: CreditCard,
        items: [
            {
                label: '2.1 LesslyKilo Pro',
                text: 'We offer premium features under a Pro subscription plan, currently priced at $10.00 USD per month or $96.00 USD per year. Prices may change from time to time, and any changes will be communicated in advance where required.',
            },
            {
                label: '2.2 Billing and Auto-Renewal',
                text: 'Pro subscriptions are billed through your app store account, such as Google Play Store or Apple App Store. Subscriptions automatically renew unless auto-renew is disabled at least 24 hours before the end of the current billing period. Renewal charges may occur within 24 hours before the current period ends.',
            },
            {
                label: '2.3 Cancellations and Refunds',
                text: 'You may manage subscriptions and turn off auto-renewal through your app store account settings. Viomath Tech Proprietary Limited does not directly process refunds for purchases made through third-party platforms; refund requests must be submitted to the relevant platform provider.',
            },
        ],
    },
    {
        id: 'accounts-security',
        title: '3. User Accounts and Security',
        icon: UserCircle2,
        items: [
            {
                label: '3.1 Account Creation',
                text: 'To use certain features, you may be required to register for an account. You agree to provide accurate, current, and complete information and to keep your account details updated.',
            },
            {
                label: '3.2 Account Responsibility',
                text: 'You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to notify us promptly if you suspect any unauthorized use or security breach.',
            },
        ],
    },
    {
        id: 'acceptable-use',
        title: '4. Acceptable Use and AI Guidelines',
        icon: Bot,
        items: [
            {
                label: 'Lawful and Respectful Use',
                text: 'You agree not to upload, submit, or transmit content that is unlawful, harmful, offensive, abusive, or that infringes the rights or privacy of others.',
            },
            {
                label: 'No Emergency or Diagnostic Reliance',
                text: 'You agree not to use the Service to obtain emergency medical support, generate definitive diagnoses, or substitute professional medical care.',
            },
            {
                label: 'No Reverse Engineering',
                text: 'You may not reverse engineer, decompile, hack, disrupt, or otherwise attempt to gain unauthorized access to the application, AI logic, backend systems, or associated infrastructure.',
            },
            {
                label: 'Compliance With Law',
                text: 'You agree to use the Service only in compliance with applicable local, national, and international laws and regulations.',
            },
            {
                label: 'Enforcement',
                text: 'We reserve the right to suspend or terminate access to the Service if we determine that your conduct violates these Terms.',
            },
        ],
    },
    {
        id: 'intellectual-property',
        title: '5. Intellectual Property',
        icon: Copyright,
        items: [
            {
                label: 'Ownership',
                text: 'The Service and its original content, features, design elements, proprietary AI prompts, logic, and functionality remain the exclusive property of Viomath Tech Proprietary Limited and its licensors.',
            },
            {
                label: 'Protected Rights',
                text: 'The Service is protected by copyright, trademark, and other applicable intellectual property laws. Our trademarks, logos, and trade dress may not be used without prior written permission.',
            },
        ],
    },
    {
        id: 'limitation-of-liability',
        title: '6. Limitation of Liability',
        icon: AlertTriangle,
        items: [
            {
                label: 'Scope of Limitation',
                text: 'To the maximum extent permitted by law, Viomath Tech Proprietary Limited and its directors, employees, partners, agents, suppliers, or affiliates shall not be liable for indirect, incidental, special, consequential, or punitive damages, including loss of profits, loss of data, loss of goodwill, personal injury, or other intangible losses arising from your use of the Service.',
            },
            {
                label: 'Use of Service',
                text: 'This limitation includes damages arising from your access to, use of, or inability to access or use the Service, any third-party conduct or content, or health complications, injuries, or dietary issues arising from reliance on Service insights.',
            },
            {
                label: 'As-Is Basis',
                text: 'The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, whether express or implied.',
            },
        ],
    },
    {
        id: 'governing-law',
        title: '7. Governing Law',
        icon: Gavel,
        items: [
            {
                label: 'Applicable Law',
                text: 'These Terms are governed and construed in accordance with the laws of the Republic of Botswana, without regard to conflict of law principles.',
            },
            {
                label: 'Waiver',
                text: 'Our failure to enforce any right or provision of these Terms does not constitute a waiver of those rights.',
            },
        ],
    },
    {
        id: 'changes',
        title: '8. Changes to Terms',
        icon: RefreshCw,
        items: [
            {
                label: 'Updates',
                text: 'We may revise or replace these Terms at our sole discretion. Continued access to or use of the Service after revised Terms become effective means you agree to be bound by the updated Terms.',
            },
        ],
    },
    {
        id: 'contact',
        title: '9. Contact Us',
        icon: Mail,
        items: [
            {
                label: 'Email',
                text: 'caselabs91@gmail.com',
            },
            {
                label: 'Company',
                text: 'Viomath Tech Proprietary Limited',
            },
        ],
    },
] as const;

const QUICK_FACTS = [
    {
        icon: ShieldCheck,
        title: 'LesslyKilo is not medical care',
        text: 'The app is designed for education, tracking, and support — not diagnosis or treatment.',
    },
    {
        icon: CreditCard,
        title: 'Subscriptions auto-renew',
        text: 'Paid plans renew through your app store unless auto-renewal is turned off in time.',
    },
    {
        icon: Bot,
        title: 'AI use must be responsible',
        text: 'The app may not be used for unlawful content, reverse engineering, or emergency medical reliance.',
    },
];

export default function TermsPage() {
    return (
        <section className="relative overflow-hidden py-24 md:py-32">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_32%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.08),transparent_28%)]" />
                <div className="absolute left-[-8%] top-20 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-10%] bottom-10 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Hero */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    className="mx-auto mb-16 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.08)]">
                        <FileCheck size={14} />
                        Terms of Service
                    </div>

                    <h1 className="text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        Clear terms for using
                        <span className="block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
              LesslyKilo responsibly
            </span>
                    </h1>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        These Terms explain the rules, responsibilities, subscription conditions, and important
                        limitations that apply when you use the LesslyKilo app and website.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <CheckCircle2 size={16} className="text-emerald-300" />
                            Effective Date: March 2, 2026
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <CheckCircle2 size={16} className="text-cyan-300" />
                            Provider: Viomath Tech Proprietary Limited
                        </div>
                    </div>
                </motion.div>

                {/* Summary block */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mb-10 rounded-[2.5rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(180deg,rgba(8,18,28,0.98),rgba(5,14,22,0.98))] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.18)] md:p-10"
                >
                    <div className="grid gap-6 lg:grid-cols-[1.35fr_0.95fr] lg:items-center">
                        <div>
                            <h2 className="text-3xl font-black text-[var(--color-brand-text)] md:text-4xl">
                                Please read these Terms before using the Service
                            </h2>
                            <p className="mt-5 text-[15px] leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                By accessing or using the LesslyKilo mobile application, website, or related
                                services, you agree to be bound by these Terms. If you do not agree with any part
                                of the Terms, you may not access or use the Service.
                            </p>
                        </div>

                        <div className="grid gap-4">
                            {QUICK_FACTS.map((fact) => {
                                const Icon = fact.icon;
                                return (
                                    <div
                                        key={fact.title}
                                        className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] p-5"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)]">
                                                <Icon size={22} />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-black uppercase tracking-[0.14em] text-[var(--color-brand-text)]">
                                                    {fact.title}
                                                </h3>
                                                <p className="mt-2 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                                    {fact.text}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>

                {/* Main layout */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* Sidebar */}
                    <motion.aside
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-80px' }}
                        className="lg:col-span-4 xl:col-span-3"
                    >
                        <div className="sticky top-24 rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,rgba(10,18,28,0.98),rgba(6,14,22,0.98))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.12)]">
                            <h3 className="mb-4 text-sm font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                On this page
                            </h3>

                            <nav className="space-y-2">
                                {SECTIONS.map((section) => {
                                    const Icon = section.icon;
                                    return (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                            className="flex items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-sm font-medium text-[var(--color-brand-text-secondary)] transition-all hover:border-[var(--color-brand-primary)]/20 hover:bg-[var(--color-brand-primary)]/8 hover:text-[var(--color-brand-text)]"
                                        >
                                            <Icon size={16} className="text-[var(--color-brand-primary)]" />
                                            <span>{section.title}</span>
                                        </a>
                                    );
                                })}
                            </nav>
                        </div>
                    </motion.aside>

                    {/* Content */}
                    <div className="space-y-6 lg:col-span-8 xl:col-span-9">
                        {SECTIONS.map((section, idx) => {
                            const Icon = section.icon;
                            return (
                                <motion.section
                                    key={section.id}
                                    id={section.id}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="show"
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ delay: idx * 0.04 }}
                                    className="rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,rgba(10,18,28,0.98),rgba(6,14,22,0.98))] p-8 shadow-[0_18px_50px_rgba(0,0,0,0.12)]"
                                >
                                    <div className="mb-6 flex items-center gap-4">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)] shadow-[0_10px_24px_rgba(34,211,238,0.10)]">
                                            <Icon size={26} />
                                        </div>

                                        <h2 className="text-2xl font-black text-[var(--color-brand-text)] md:text-3xl">
                                            {section.title}
                                        </h2>
                                    </div>

                                    <div className="space-y-5">
                                        {section.items.map((item) => (
                                            <div
                                                key={item.label}
                                                className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)]/70 p-5"
                                            >
                                                <h3 className="text-sm font-black uppercase tracking-[0.16em] text-[var(--color-brand-primary)]">
                                                    {item.label}
                                                </h3>
                                                <p className="mt-3 text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-[15px]">
                                                    {item.text}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </motion.section>
                            );
                        })}
                    </div>
                </div>

                {/* Footer strip */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="mt-12 rounded-[2rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(16,185,129,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.16)] md:p-8"
                >
                    <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                        <div>
                            <h3 className="text-xl font-black text-[var(--color-brand-text)] md:text-2xl">
                                Questions about these Terms?
                            </h3>
                            <p className="mt-2 max-w-3xl text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-base">
                                If you have questions about the Terms of Service or how they apply to your use of
                                LesslyKilo, please contact us.
                            </p>
                        </div>

                        <a
                            href="mailto:caselabs91@gmail.com"
                            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-[var(--color-brand-text)] transition-all hover:border-emerald-300/30 hover:bg-emerald-400/14"
                        >
                            <Mail size={16} className="text-emerald-300" />
                            caselabs91@gmail.com
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}